import React, { useContext, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import clsx from 'clsx';
import { GatewayDefinition } from '@moderepo/mode-apis';
import { fetchRCProjectGatewayCatalog, rcDataContext, selectProjectGatewayCatalog } from '@moderepo/mode-data-state-robot-cloud';
import { fetchProjectDeviceById, ritzDataContext, selectProjectById, selectProjectDeviceById } from '@moderepo/mode-data-state-ritz';
import { RCDeviceCollectionsLatestValuesDisplayContainer } from './RCDeviceCollectionsLatestValuesDisplayContainer';
import { useRitzTranslation } from '../../utils/customHooks';

/**
 * This is a page that show the latest data for all collections belonged to a device.
 */
export const RCDeviceCollectionsLatestValuesDisplayPage: React.FC = () => {
    // Get the params from the route or url
    const params = useParams();
    const projectId = Number(params.projectId);
    const userId = params.userId ? Number(params.userId) : undefined;   // these 2 params are optional depending on which route the user is in
    const homeId = params.homeId ? Number(params.homeId) : undefined;
    const deviceId = Number(params.deviceId);

    const { state:dataState, dispatch:dataDispatch } = useContext(ritzDataContext);
    const { state:rcDataState, dispatch:rcDataDispatch } = useContext(rcDataContext);
    const project = selectProjectById(dataState, projectId);
    const selectedDevice = deviceId ? selectProjectDeviceById(dataState, deviceId) : undefined;
    const { trans } = useRitzTranslation(project);


    // The child's action that needs to be shown in the page's title bar as the main action
    // This is a way for the page to show a UI component created and controlled by a child components
    const [pageMainAction, setPageMainAction] = useState<JSX.Element | undefined>();


    // Get Gateway Catalog
    const gatewayCatalog = selectProjectGatewayCatalog(rcDataState, projectId);

    // find the gatewayDefinition for the selected device from the gateway catalog. If we find a gatewayDefinition, that mean the device is a
    // gateway and we should show different UI.
    const isGateway = useMemo(() => {
        if (gatewayCatalog && selectedDevice) {
            return gatewayCatalog.find((gatewayDef: GatewayDefinition) => {
                return gatewayDef.deviceClass === selectedDevice.deviceClass;
            }) !== undefined;
        }

        return false;
    }, [gatewayCatalog, selectedDevice]);


    /**
     * This useEffect is used for loading gateway definition on component init
     */
    useEffect(() => {
        if (!gatewayCatalog && project) {
            rcDataDispatch(fetchRCProjectGatewayCatalog(project.id)).catch(() => {
                /* ignore error */
            });
        }
    }, [gatewayCatalog, project, rcDataDispatch]);


    /**
     * This useEffect is used for loading device data on component init
     */
    useEffect(() => {
        if (!selectedDevice) {
            dataDispatch(fetchProjectDeviceById(projectId, deviceId)).catch(() => { /* ignore error */ });
        }
    }, [dataDispatch, deviceId, projectId, selectedDevice]);


    return (
        <div className={clsx('page-main-content')}>
            <div className="title-bar">
                <div className="title-bar-item title-text">
                    <div className="page-title">
                        {trans('page.title.project_device_collection_data')}
                    </div>
                    <div className="page-subtitle">
                        {trans('page.subtitle.project_device_collection_data', { term: trans(isGateway ? 'terms.gateway' : 'terms.device') })}
                    </div>
                </div>
                {pageMainAction}
            </div>

            <RCDeviceCollectionsLatestValuesDisplayContainer
                projectId={projectId}
                deviceId={deviceId}
                userId={userId}
                homeId={homeId}
                setPageMainAction={setPageMainAction}
            />
        </div>
    );
};
